import React from "react";
import { Check, FilePdf } from "@phosphor-icons/react";

/**
 * Direct competitor benchmarking table (price-free).
 * @param {{rows:Array, competitors:Array, winners:Object}} benchmark
 * @param {(competitor)=>string|null} datasheetUrl - link to evidence PDF
 * @param {(competitor)=>Function} [onOpenDatasheet] - optional click handler factory (authenticated blob open)
 */
export default function CompetitorTable({ benchmark, datasheetUrl, onOpenDatasheet }) {
  const rows = benchmark?.rows || [];
  const competitors = benchmark?.competitors || [];
  const winners = benchmark?.winners || {};
  if (rows.length === 0 || competitors.length === 0) return null;

  const cols = `minmax(140px, 200px) repeat(${competitors.length + 1}, minmax(130px, 1fr))`;

  const Cell = ({ value, win }) => (
    <div
      className={`p-4 border-l border-zinc-100 flex items-center gap-1.5 ${
        win ? "bg-emerald-50" : ""
      }`}
    >
      {win && <Check size={15} weight="bold" className="text-emerald-600 shrink-0" />}
      <span className={`text-sm ${win ? "font-bold text-emerald-900" : "text-zinc-700"}`}>
        {value || "—"}
      </span>
    </div>
  );

  return (
    <section className="mt-12" data-testid="competitor-table">
      <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight text-zinc-900">
        How we compare
      </h2>
      <p className="text-sm text-zinc-500 mb-6">
        Spec-by-spec against direct competitors. Superior specs are highlighted; figures backed by
        official test data sheets where shown.
      </p>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <div style={{ minWidth: 200 + (competitors.length + 1) * 130 }}>
          {/* Header */}
          <div className="grid border-b border-zinc-200 bg-zinc-50" style={{ gridTemplateColumns: cols }}>
            <div className="p-4 flex items-end">
              <span className="text-[11px] uppercase tracking-wider text-zinc-400">Specification</span>
            </div>
            <div className="p-4 border-l border-zinc-100 bg-[#0047AB]/5" data-testid="competitor-col-ours">
              <div className="text-sm font-black text-[#0047AB]">STON LINK</div>
              <div className="text-[11px] text-zinc-500">Our product</div>
            </div>
            {competitors.map((c) => (
              <div key={c.competitor_id} className="p-4 border-l border-zinc-100" data-testid={`competitor-col-${c.competitor_id}`}>
                <div className="text-sm font-bold text-zinc-900">{c.name || "Competitor"}</div>
                {c.product_name && <div className="text-[11px] text-zinc-500">{c.product_name}</div>}
                {datasheetUrl && datasheetUrl(c) && (
                  <a
                    href={datasheetUrl(c)}
                    target="_blank"
                    rel="noreferrer"
                    onClick={onOpenDatasheet ? onOpenDatasheet(c) : undefined}
                    data-testid={`competitor-datasheet-${c.competitor_id}`}
                    className="inline-flex items-center gap-1 mt-1.5 text-[11px] font-medium text-[#0047AB] hover:underline"
                  >
                    <FilePdf size={13} weight="fill" /> Data sheet
                  </a>
                )}
              </div>
            ))}
          </div>

          {/* Spec rows */}
          {rows.map((r) => {
            const w = winners[r.row_id];
            return (
              <div
                key={r.row_id}
                className="grid border-b border-zinc-100 last:border-b-0"
                style={{ gridTemplateColumns: cols }}
                data-testid={`competitor-row-${r.row_id}`}
              >
                <div className="p-4 flex items-center">
                  <span className="text-sm font-medium text-zinc-800">{r.label}</span>
                </div>
                <Cell value={r.ours} win={w === "ours"} />
                {competitors.map((c) => (
                  <Cell key={c.competitor_id} value={c.values?.[r.row_id]} win={w === c.competitor_id} />
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
